/**
 * ExportersCard
 *
 * A reusable card component that displays attorney export statistics.
 * Can be configured for either "high" or "low" exporters via the mode prop.
 *
 * Features:
 * - Stacked bar chart showing exported vs unexported hours
 * - Configurable threshold filter
 * - Display limit filter
 * - Summary statistics grid
 * - Collapsible detailed table
 */

import { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { ChevronRight } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  AttorneyExportStats,
  TIMEFRAME_OPTIONS,
} from "../types/dashboard";
import {
  CHART_COLORS,
  CHART_CONFIG,
  TEXT_COLORS,
  SUMMARY_CARD_STYLES,
} from "../config/dashboardTheme";

// ============================================
// TYPES
// ============================================

export interface ExportersCardProps {
  /** Which mode to display: "high" filters for >= threshold, "low" filters for < threshold */
  mode: "high" | "low";
  /** Array of attorney export statistics */
  attorneys: AttorneyExportStats[];
  /** Whether data is currently loading */
  loading?: boolean;
  /** Current timeframe selection */
  timeframe: string;
  /** Callback when timeframe changes */
  onTimeframeChange: (timeframe: string) => void;
}

// ============================================
// CONFIG BY MODE
// ============================================

interface ModeConfig {
  title: string;
  thresholdOptions: { value: number; label: string }[];
  defaultThreshold: number;
  filterFn: (attorney: AttorneyExportStats, threshold: number) => boolean;
  emptyMessage: (threshold: number) => string;
  successMessage: (threshold: number) => string;
  badgeStyle: (pct: number) => string;
  thresholdLabel: (threshold: number, count: number) => string;
}

const MODE_CONFIG: Record<"high" | "low", ModeConfig> = {
  high: {
    title: "High Exporters",
    thresholdOptions: [
      { value: 50, label: ">50%" },
      { value: 60, label: ">60%" },
      { value: 70, label: ">70%" },
      { value: 80, label: ">80%" },
      { value: 90, label: ">90%" },
    ],
    defaultThreshold: 50,
    filterFn: (a, threshold) => a.exported_pct >= threshold,
    emptyMessage: (threshold) =>
      `No attorneys with ≥ ${threshold}% export rate found in this period.`,
    successMessage: () => "",
    badgeStyle: (pct) =>
      pct >= 90
        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
        : pct >= 75
          ? "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"
          : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    thresholdLabel: (threshold, count) =>
      `${count} attorney${count !== 1 ? "s" : ""} with ≥ ${threshold}% export rate`,
  },
  low: {
    title: "Low Exporters",
    thresholdOptions: [
      { value: 50, label: "<50%" },
      { value: 40, label: "<40%" },
      { value: 30, label: "<30%" },
      { value: 20, label: "<20%" },
      { value: 10, label: "<10%" },
    ],
    defaultThreshold: 50,
    filterFn: (a, threshold) => a.exported_pct < threshold,
    emptyMessage: () => "No attorney data available.",
    successMessage: (threshold) =>
      `All attorneys have exported at least ${threshold}% of their time. Great job!`,
    badgeStyle: (pct) =>
      pct < 25
        ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
        : pct < 40
          ? "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300"
          : "bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
    thresholdLabel: (threshold, count) =>
      `${count} attorney${count !== 1 ? "s" : ""} with < ${threshold}% export rate`,
  },
};

// ============================================
// DISPLAY LIMIT OPTIONS
// ============================================

const DISPLAY_LIMIT_OPTIONS = [
  { value: 25, label: "Top 25" },
  { value: 50, label: "Top 50" },
  { value: 100, label: "Top 100" },
  { value: 0, label: "All" },
];

// ============================================
// COMPONENT
// ============================================

export default function ExportersCard({
  mode,
  attorneys,
  loading = false,
  timeframe,
  onTimeframeChange,
}: ExportersCardProps) {
  const config = MODE_CONFIG[mode];

  // State
  const [threshold, setThreshold] = useState(config.defaultThreshold);
  const [displayLimit, setDisplayLimit] = useState(0); // 0 = show all
  const [isTableExpanded, setIsTableExpanded] = useState(true); // Open by default

  // Filter attorneys based on mode and threshold
  const filteredByThreshold = useMemo(() => {
    const filtered = attorneys.filter((a) => config.filterFn(a, threshold));
    // Sort: For high exporters, highest % first. For low exporters, also highest % first (best of the low)
    return filtered.sort((a, b) => b.exported_pct - a.exported_pct);
  }, [attorneys, threshold, config]);

  // Apply display limit
  const displayedAttorneys = useMemo(() => {
    if (displayLimit === 0) return filteredByThreshold;
    return filteredByThreshold.slice(0, displayLimit);
  }, [filteredByThreshold, displayLimit]);

  // Transform for bar chart
  const chartData = useMemo(() => {
    return displayedAttorneys.map((a) => ({
      name: a.name.length > 12 ? a.name.substring(0, 12) + "..." : a.name,
      fullName: a.name,
      exported: a.exported_hours,
      unexported: a.unexported_hours,
      total: a.total_hours,
      exportPct: a.exported_pct,
    }));
  }, [displayedAttorneys]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const totalExported = displayedAttorneys.reduce(
      (sum, a) => sum + a.exported_hours,
      0
    );
    const totalUnexported = displayedAttorneys.reduce(
      (sum, a) => sum + a.unexported_hours,
      0
    );
    const totalHours = displayedAttorneys.reduce(
      (sum, a) => sum + a.total_hours,
      0
    );
    return {
      count: displayedAttorneys.length,
      totalExported: totalExported.toFixed(1),
      totalUnexported: totalUnexported.toFixed(1),
      totalHours: totalHours.toFixed(1),
    };
  }, [displayedAttorneys]);

  // Select dropdown styling
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: "right 0.5rem center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "1.25em 1.25em",
    paddingRight: "2rem",
  };

  // Custom tooltip for the chart
  const CustomBarTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: (typeof chartData)[0] }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-sm">
          <p className="font-semibold mb-2">{data.fullName}</p>
          <div className="space-y-1">
            <p className={TEXT_COLORS.exported.light}>
              Exported: {data.exported.toFixed(1)} hrs
            </p>
            <p className={TEXT_COLORS.unexported.light}>
              Not Exported: {data.unexported.toFixed(1)} hrs
            </p>
            <p className="text-muted-foreground">
              Total: {data.total.toFixed(1)} hrs
            </p>
            <p className="font-medium">Export Rate: {data.exportPct}%</p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col space-y-1.5 p-6 pb-4">
        <div className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <h3 className="text-lg font-semibold leading-none tracking-tight">
            {config.title}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Threshold Filter */}
            <select
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              className="h-9 px-3 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer appearance-none"
              style={selectStyle}
            >
              {config.thresholdOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {/* Timeframe Filter */}
            <select
              value={timeframe}
              onChange={(e) => onTimeframeChange(e.target.value)}
              className="h-9 px-3 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer appearance-none"
              style={selectStyle}
            >
              {TIMEFRAME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {/* Display Limit Filter */}
            <select
              value={displayLimit}
              onChange={(e) => setDisplayLimit(Number(e.target.value))}
              className="h-9 px-3 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer appearance-none"
              style={selectStyle}
            >
              {DISPLAY_LIMIT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pt-0 space-y-4 flex-1 flex flex-col">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading export statistics...
          </div>
        ) : (
          <>
            {/* Stacked Bar Chart */}
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={CHART_COLORS.grid.line}
                    className="opacity-50"
                  />
                  <XAxis
                    dataKey="name"
                    tick={false}
                    axisLine={{ stroke: CHART_COLORS.grid.axis, strokeWidth: 1 }}
                    tickLine={false}
                  />
                  <YAxis
                    label={{
                      value: "Hours",
                      angle: -90,
                      position: "insideLeft",
                      style: { textAnchor: "middle", fill: CHART_COLORS.grid.axis, fontSize: 12 },
                    }}
                    tick={{ fontSize: 12, fill: CHART_COLORS.grid.axis }}
                    axisLine={{ stroke: CHART_COLORS.grid.axis, strokeWidth: 1 }}
                    tickLine={{ stroke: CHART_COLORS.grid.axis }}
                  />
                  {chartData.length > 0 && <Tooltip content={<CustomBarTooltip />} />}
                  <Legend
                    verticalAlign="top"
                    height={36}
                    content={({ payload }) => (
                      <div className="flex justify-center gap-6 text-sm">
                        {payload?.map((entry, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-sm"
                              style={{
                                backgroundColor:
                                  entry.value === "Exported"
                                    ? CHART_COLORS.exported.stroke
                                    : CHART_COLORS.unexported.stroke,
                              }}
                            />
                            <span className="text-foreground">
                              {entry.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                  <ReferenceLine y={0} stroke={CHART_COLORS.grid.axis} />
                  <Bar
                    dataKey="exported"
                    stackId="hours"
                    fill={CHART_COLORS.exported.fill}
                    maxBarSize={CHART_CONFIG.maxBarSize}
                    name="Exported"
                    radius={CHART_CONFIG.bottomBarRadius}
                  />
                  <Bar
                    dataKey="unexported"
                    stackId="hours"
                    fill={CHART_COLORS.unexported.fill}
                    maxBarSize={CHART_CONFIG.maxBarSize}
                    name="Not Exported"
                    radius={CHART_CONFIG.topBarRadius}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Count Card */}
              <div className={SUMMARY_CARD_STYLES.base}>
                <div className="flex items-center gap-2.5">
                  <div className={cn("w-1 h-8 rounded-full flex-shrink-0", SUMMARY_CARD_STYLES.accent.count)} />
                  <div className="min-w-0">
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide leading-tight">
                      {mode === "high" ? (<>High<br />Exporters</>) : (<>Low<br />Exporters</>)}
                    </div>
                    <div className="text-xl font-semibold text-muted-foreground">
                      {summaryStats.count}
                    </div>
                  </div>
                </div>
              </div>
              {/* Total Exported Card */}
              <div className={SUMMARY_CARD_STYLES.base}>
                <div className="flex items-center gap-2.5">
                  <div className={cn("w-1 h-8 rounded-full flex-shrink-0", SUMMARY_CARD_STYLES.accent.exported)} />
                  <div className="min-w-0">
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide leading-tight">
                      Total<br />Exported
                    </div>
                    <div className={cn("text-xl font-semibold", TEXT_COLORS.exported.value)}>
                      {summaryStats.totalExported} <span className="text-xs font-normal text-muted-foreground">hrs</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Total Unexported Card */}
              <div className={SUMMARY_CARD_STYLES.base}>
                <div className="flex items-center gap-2.5">
                  <div className={cn("w-1 h-8 rounded-full flex-shrink-0", SUMMARY_CARD_STYLES.accent.unexported)} />
                  <div className="min-w-0">
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide leading-tight">
                      Total<br />Unexported
                    </div>
                    <div className={cn("text-xl font-semibold", TEXT_COLORS.unexported.value)}>
                      {summaryStats.totalUnexported} <span className="text-xs font-normal text-muted-foreground">hrs</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Total Hours Card */}
              <div className={SUMMARY_CARD_STYLES.base}>
                <div className="flex items-center gap-2.5">
                  <div className={cn("w-1 h-8 rounded-full flex-shrink-0", SUMMARY_CARD_STYLES.accent.neutral)} />
                  <div className="min-w-0">
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide leading-tight">
                      Total<br />Hours
                    </div>
                    <div className="text-xl font-semibold text-muted-foreground">
                      {summaryStats.totalHours} <span className="text-xs font-normal">hrs</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Collapsible Table */}
            <div className="pt-3 flex-1 flex flex-col">
              <button
                onClick={() => setIsTableExpanded(!isTableExpanded)}
                className="w-full flex items-center gap-2 py-3 px-3.5 bg-card border border-border hover:bg-muted/30 rounded-lg transition-colors shadow-sm"
              >
                <ChevronRight
                  className={cn(
                    "w-4 h-4 text-muted-foreground transition-transform",
                    isTableExpanded && "rotate-90"
                  )}
                />
                <span className="text-sm font-medium text-muted-foreground">
                  Detailed Breakdown
                </span>
                <span className="text-xs text-muted-foreground">
                  ({displayedAttorneys.length} attorneys)
                </span>
              </button>
              {isTableExpanded && (
                <div className="overflow-x-auto mt-3 flex-1">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 font-medium">
                          Attorney
                        </th>
                        <th className="text-right py-2 px-3 font-medium">
                          Exported
                        </th>
                        <th className="text-right py-2 px-3 font-medium">
                          Not Exported
                        </th>
                        <th className="text-right py-2 px-3 font-medium">
                          Total Hours
                        </th>
                        <th className="text-right py-2 px-3 font-medium">
                          Export %
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedAttorneys.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-muted-foreground">
                            {attorneys.length === 0
                              ? "No attorney data available for this period."
                              : config.successMessage(threshold)}
                          </td>
                        </tr>
                      ) : (
                        displayedAttorneys.map((attorney) => (
                          <tr
                            key={attorney.prof_id}
                            className="border-b border-border hover:bg-muted/50"
                          >
                            <td className="py-2 px-3">{attorney.name}</td>
                            <td className={cn("py-2 px-3 text-right", TEXT_COLORS.exported.light)}>
                              {attorney.exported_hours.toFixed(1)}
                            </td>
                            <td className={cn("py-2 px-3 text-right", TEXT_COLORS.unexported.light)}>
                              {attorney.unexported_hours.toFixed(1)}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {attorney.total_hours.toFixed(1)}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <span
                                className={cn(
                                  "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                                  config.badgeStyle(attorney.exported_pct)
                                )}
                              >
                                {attorney.exported_pct}%
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
